import React, { useState,useContext } from 'react';
import { useApiFetch } from '../../utils/useApiFetch';
import { TokenContext } from '../../Context/TokenContext';
import { getApiBaseUrl } from '../../utils/Config';
import { AlertContext } from '../../Context/AlertContext';
import { useIsAuthenticated } from "../../utils/msalCompat";
import { CandidateContext } from '../../Context/CandidateContext';

function ExperienceForm({ candidateData, onCancel, handleUpdate, hasCurrent }) {
    const isAuthenticated = useIsAuthenticated();
    const apiFetch = useApiFetch();
    const { refetchCandidateDetails } = useContext(CandidateContext)
    const { idTokenClaims } = useContext(TokenContext);
    const [saving, setSaving] = useState(false);
    const { showAlert } = useContext(AlertContext);

    const [formData, setFormData] = useState({
        Title: candidateData.Title || '',
        Company: candidateData.Company || '',
        Location: candidateData.Location || '',
        StartDate: candidateData.StartDate || '',
        EndDate: candidateData.EndDate || '',
        Current: candidateData.Current === 'true' || candidateData.Current === true,
        About: candidateData.About || '',
        ExperienceID: candidateData.ExperienceID?.toString() || ''
    });
    const [errors, setErrors] = useState({
        Title: '',
        Company: '',
        Location: '',
        StartDate: '',
        EndDate: '',
        Current: '',
        About: '',
    });
    const handleInputChange = (event, fieldName) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;

        if (fieldName === "StartDate" || fieldName === "EndDate") {
            // Ensure it's a valid date format
            if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                setFormData((prevData) => ({
                    ...prevData,
                    [fieldName]: value,
                }));
                return;
            }

            const enteredDate = new Date(value);
            const year = enteredDate.getFullYear();

            if (year < 1900 || year > 2100) {
                showAlert("Please enter a valid year between 1900 and 2100.", false);
                return;
            }
        }

        setFormData((prevData) => ({
            ...prevData,
            [fieldName]: value,
        }));
    };
    const baseUrl = getApiBaseUrl();

    const formattedStartDate = formData.StartDate ? new Date(formData.StartDate).toISOString().split('T')[0] : '';
    const formattedEndDate = formData.EndDate ? new Date(formData.EndDate).toISOString().split('T')[0] : '';

    //Validation
    const validate = () => {
        const newErrors = {};

        // Allows letters, numbers, spaces, dashes, pipes, commas, and periods
        const allowedRegex = /^[a-zA-Z0-9\s,'-.|]+$/;

        const formattedStartDate = formData.StartDate ? new Date(formData.StartDate).toISOString().split('T')[0] : '';
        const formattedEndDate = formData.EndDate ? new Date(formData.EndDate).toISOString().split('T')[0] : '';

        // Check for required fields
        if (!formData.Title) newErrors.Title = 'Title is required.';
        if (!formData.Company) newErrors.Company = 'Company is required.';
        if (!formData.Location) newErrors.Location = 'Location is required.';
        if (!formData.StartDate) newErrors.StartDate = 'Start date is required.';

        // If not checked as current, end date is required
        if (!formData.Current && !formData.EndDate) {
            newErrors.EndDate = 'End date is required.';
        }

        // Disallow invalid characters
        if (formData.Title && !allowedRegex.test(formData.Title)) {
            newErrors.Title = 'Title contains invalid characters.';
        }
        if (formData.Company && !allowedRegex.test(formData.Company)) {
            newErrors.Company = 'Company name contains invalid characters.';
        }
        if (formData.Location && !allowedRegex.test(formData.Location)) {
            newErrors.Location = 'Location contains invalid characters.';
        }

        // Ensure End Date is after Start Date
        if (formData.StartDate && formData.EndDate && new Date(formattedStartDate) > new Date(formattedEndDate)) {
            newErrors.EndDate = 'End date should be after start date.';
        }

        // If Current Role is checked, EndDate shouldn't be required or considered
        if (formData.Current) {
            delete newErrors.EndDate;
        }

        // Prevent multiple current roles
        if (hasCurrent && formData.Current) {
            newErrors.Current = 'You can only have one current role.';
        }

        // Length constraints
        if (formData.Title && formData.Title.length > 100) {
            newErrors.Title = 'Title should not exceed 100 characters.';
        }
        if (formData.Company && formData.Company.length > 100) {
            newErrors.Company = 'Company should not exceed 100 characters.';
        }
        if (formData.Location && formData.Location.length > 100) {
            newErrors.Location = 'Location should not exceed 100 characters.';
        }
        if (formData.About && formData.About.length > 1000) {
            newErrors.About = 'Description should not exceed 1000 characters.';
        }

        setErrors(newErrors);
        return Object.values(newErrors).every(error => !error);
    };


    //Submit to API
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            if (saving) {
                return;
            }
            setSaving(true);

            const dataToPost = {
                Title: formData.Title,
                Company: formData.Company,
                Location: formData.Location,
                StartDate: formData.StartDate,
                EndDate: formData.EndDate,
                Current: formData.Current,
                About: formData.About,
                // Set other properties here
                CandidateID: candidateData.CandidateID,
                ExperienceID: candidateData.ExperienceID,
                DateTimeCreated: new Date().toISOString(),
                DateTimeModified: new Date().toISOString(),
                CreatedBy: 'Admin',
                ModifiedBy: 'User',
                IsDeleted: false,
                AuthUserID: idTokenClaims.oid,
            };
            if (!formData.Current && formData.EndDate) {
                dataToPost.EndDate = formData.EndDate;
            }
            try {
                const response = await apiFetch(
                    `${baseUrl}/api/Experience/Upsert`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(dataToPost),
                    }
                );
                const responseData = await response.json();
                if (responseData.success) {
                    showAlert('Saved successfully.', true);
                    refetchCandidateDetails();
                } else {
                    showAlert(responseData.message || 'Something went wrong. Please try again.', false);
                }
            }
            catch (error) {
                console.log('Fetch error:', error);

                showAlert('Something went wrong. Please try again.', false);
            }
            finally {
                setSaving(false);
            }
        }
    };
    if (!isAuthenticated) {
        return <div>Please log in to access the dashboard.</div>;
    }
    return (
        <div className="experience-create-form">
            <form onSubmit={handleSubmit}>
                <div className="experience-item-edit">
                    <div className="experience-edit card-body-iajj">
                        <div className="row g-5">
                            <div className="col-md-6">
                                <div>
                                    <label className="form-label">Job Title</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.Title}
                                        onChange={e => handleInputChange(e, 'Title')} />
                                    {errors.Title && (
                                        <small className="text-danger">{errors.Title}</small>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div>
                                    <label className="form-label">Company</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.Company}
                                        onChange={e => handleInputChange(e, 'Company')} />
                                    {errors.Company && (
                                        <small className="text-danger">{errors.Company}</small>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div>
                                    <label className="form-label">Location</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.Location}
                                        onChange={e => handleInputChange(e, 'Location')} />
                                    {errors.Location && (
                                        <small className="text-danger">{errors.Location}</small>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div>
                                    <label className="form-label">Start Date</label>
                                    <input
                                        type="date"
                                        min="1900-01-01"
                                        max="2100-12-31"
                                        className="form-control flatpickr-input active"
                                        value={formattedStartDate}
                                        onChange={e => handleInputChange(e, 'StartDate')} />
                                    {errors.StartDate && (
                                        <small className="text-danger">{errors.StartDate}</small>
                                    )}
                                </div>
                            </div>
                            {!formData.Current && (
                                <div className="col-md-6">
                                    <div>
                                        <label className="form-label">Completion Date</label>
                                        <input
                                            type="date"
                                            min="1900-01-01"
                                            max="2100-12-31"
                                            className="form-control flatpickr-input active"
                                            value={formattedEndDate}
                                            onChange={e => handleInputChange(e, 'EndDate')} />
                                        {errors.EndDate && (
                                            <small className="text-danger">{errors.EndDate}</small>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="col-md-6">
                                <label className="form-label">Current Role</label>
                                <div className="form-check form-check-linethrough">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={formData.Current}
                                        onChange={e => handleInputChange(e, 'Current')} />
                                    {errors.Current && (
                                        <small className="text-danger">{errors.Current}</small>
                                    )}
                                </div>
                            </div>
                            <div className="col-12">
                                <div>
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className="form-control"
                                        value={formData.About}
                                        onChange={e => handleInputChange(e, 'About')}></textarea>
                                    {errors.About && (
                                        <small className="text-danger">{errors.About}</small>
                                    )}
                                </div>
                            </div>
                            <div className="col-12 text-end">
                                <button type="submit" className="btn btn-sm btn-primary me-2 add-experience" id="add-experience" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                                <button type="button" className="btn btn-sm btn-neutral me-2 cancel-add-experience" onClick={onCancel}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default ExperienceForm;
